import React from 'react';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CTB from '~/components/CTB';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { productTypes } from '~/hooks/useCtbProductInfo';
import useResponsive from '~/hooks/useResponsive';

import { CardActionType, SEVERITY_COLORS } from './utils/constants';
import { CardAction, CardTopBannerProps } from './utils/types';

interface MobileBannerContentProps {
  title: CardTopBannerProps['title'];
}

const MobileBannerContent = ({ title }: MobileBannerContentProps) => (
  <Typography variant="h3" fontWeight={700} textAlign="center">
    {title}
  </Typography>
);

interface DesktopBannerContentProps {
  icon: React.ReactNode;
  title: CardTopBannerProps['title'];
  description?: CardTopBannerProps['description'];
}

const DesktopBannerContent = ({
  icon,
  title,
  description = '',
}: DesktopBannerContentProps) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
    {icon}
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ minWidth: 0, flexWrap: 'wrap' }}
    >
      <Typography variant="h3" fontWeight={700}>
        {title}
      </Typography>
      {description && <Typography variant="body1">{description}</Typography>}
    </Stack>
  </Stack>
);

const CardTopBanner = ({
  title,
  description = '',
  severity,
  icon = undefined,
  backgroundColor: backgroundColorProp = '',
  accentColor: accentColorProp = '',
  actions = [],
  align = 'start',
  ctbData = {
    prodInstId: '',
    parentProdInstId: '',
    productName: '',
    currentPlan: '',
    domainName: '',
  },
  children = undefined,
  isHangingBanner = false,
  variant = 'default',
  ctaButton = undefined,
  borderColor = '',
  layoutBreakpoint = 'sm',
  ...props
}: CardTopBannerProps) => {
  const severityColors = severity ? SEVERITY_COLORS[severity] : undefined;
  const accentColor = accentColorProp || severityColors?.accentColor || '';
  const backgroundColor =
    backgroundColorProp || severityColors?.backgroundColor || '';

  const { isMobile: isMobileView, isTablet: isTabletView } = useResponsive();
  const useMobileBannerContent =
    layoutBreakpoint === 'md'
      ? isMobileView() || isTabletView()
      : isMobileView();
  const iconElement = icon ?? (
    <ErrorOutlineIcon sx={{ color: accentColor }} width={24} height={24} />
  );
  const { brandFromJWT: brand } = useBrandInfo();
  const isBluehost = brand?.toLowerCase() === 'bluehost';
  const borderColorForHangingBanner = isBluehost ? 'error.100' : '#E84A50';

  if (variant === 'cta-left' && ctaButton) {
    const {
      label,
      color = 'primary',
      testId,
      product = productTypes.HOSTING_UPGRADE,
      ctbData: buttonCtbData,
      borderColor: bannerBorderColor,
      borderColorBottom,
    } = ctaButton;
    return (
      <Stack
        direction="row"
        alignItems="center"
        px={3}
        py={2}
        sx={{
          bgcolor: backgroundColor,
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          border: '1px solid',
          borderColor: bannerBorderColor,
          borderBottomColor: borderColorBottom,
          mx: '24px',
        }}
        data-testid="card-top-banner-cta-left"
      >
        <CTB product={product} data={buttonCtbData ?? {}}>
          {/* @ts-expect-error */}
          <Button
            variant="contained"
            color={color}
            size="small"
            data-testid={testId}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {label}
          </Button>
        </CTB>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 2, borderColor: 'border.dark' }}
        />
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
        >
          <Typography
            variant="h3"
            fontWeight={700}
            data-testid="card-top-banner-cta-left-heading"
          >
            {title}
          </Typography>
          {description && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ display: { xs: 'none', sm: 'inline' } }}
              data-testid="card-top-banner-cta-left-description"
            >
              - {description}
            </Typography>
          )}
        </Stack>
      </Stack>
    );
  }

  const getActionButtonSx = (
    variant: CardAction['variant'],
    color?: string,
  ) => ({
    borderColor: variant === 'outlined' ? color ?? accentColor : undefined,
    color: variant !== 'contained' ? color ?? accentColor : undefined,
    height: '28px !important',
    backgroundColor:
      variant === 'contained' ? color ?? accentColor : 'transparent',
    '&:hover':
      variant === 'contained'
        ? { backgroundColor: color ?? accentColor, boxShadow: 'none' }
        : undefined,
  });

  const renderAction = (action: CardAction, key: React.Key) => {
    const {
      label,
      variant = 'outlined',
      color,
      loading,
      disabled,
      onClick,
    } = action;

    const sx = getActionButtonSx(variant, color);

    if (action.type === CardActionType.HOSTING_UPGRADE) {
      return (
        <CTB
          key={key}
          product={CardActionType.HOSTING_UPGRADE}
          data={{ ...ctbData, ...(props as CardTopBannerProps)?.ctbData }}
          sx={{
            textDecoration: 'none',
          }}
        >
          {/* @ts-ignore */}
          <LoadingButton variant={variant} loading={loading} sx={sx} fullWidth>
            {label}
          </LoadingButton>
        </CTB>
      );
    }

    if (action.type === CardActionType.MONARX_FAMILY) {
      return (
        <CTB
          key={key}
          product={CardActionType.MONARX_FAMILY}
          data={{ ...ctbData, ...(props as CardTopBannerProps)?.ctbData }}
          sx={{
            textDecoration: 'none',
          }}
        >
          {/* @ts-ignore */}
          <LoadingButton
            loading={loading}
            variant={variant}
            sx={sx}
            fullWidth
            style={{ backgroundColor: 'transparent' }}
          >
            {label}
          </LoadingButton>
        </CTB>
      );
    }

    return (
      <LoadingButton
        key={key}
        onClick={onClick}
        loading={loading}
        variant={variant}
        disabled={disabled}
        sx={{
          ...sx,
          width: { xs: '100%', [layoutBreakpoint]: 'auto' },
        }}
        data-testid={
          action.testId ??
          `${action.label.toLowerCase().replace(' ', '-')}-button`
        }
      >
        {label}
      </LoadingButton>
    );
  };

  return (
    <Stack
      spacing={2}
      direction={{ xs: 'column', [layoutBreakpoint]: 'row' }}
      alignItems="center"
      justifyContent="space-between"
      p={1.25}
      bgcolor={backgroundColor}
      data-testid="card-top-banner"
      data-severity={severity}
      sx={{
        ...(isHangingBanner
          ? {
              borderRadius: '12px',
              border: '1px solid',
              borderColor: borderColorForHangingBanner,
            }
          : {
              borderRadius: '12px 12px 0 0',
              border: '1px solid',
              borderColor: borderColor || 'border.dark',
              borderBottom: 'none',
              mx: '24px',
            }),
      }}
    >
      <Stack
        sx={{
          minWidth: 0,
          flexShrink: 0,
          width: { xs: '100%', [layoutBreakpoint]: 'auto' },
        }}
      >
        {useMobileBannerContent ? (
          <MobileBannerContent title={title} />
        ) : (
          <DesktopBannerContent
            icon={iconElement}
            title={title}
            description={description}
          />
        )}
      </Stack>

      {children}

      {actions.length > 0 && (
        <Stack
          direction={{ xs: 'column', [layoutBreakpoint]: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', [layoutBreakpoint]: 'center' }}
          flexWrap="wrap"
          sx={{
            width: { xs: '100%', [layoutBreakpoint]: 'auto' },
            flexShrink: 0,
          }}
        >
          {actions.map((action, index) =>
            renderAction(action, `${action.label}-${index}`),
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default CardTopBanner;
